import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { apiDelete, apiPatch, apiPost, apiPut } from "../utils/api";
import {
  getAdminAccessToken,
  getOwnerAccessToken,
  getStoreAccessToken,
} from "../utils/auth";
import { showError, showSuccess } from "../utils/alert";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

type Method = "PUT" | "PATCH" | "POST" | "DELETE";

export function useUpdateResource() {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const updateResource = async (
    url: string,
    data: any,
    token: "owner" | "admin" | "store",
    options?: {
      method?: Method;
      invalidateQueries?: string[];
      successMessage?: string;
      navigateAfter?: () => void;
      confirmMessage?: string; // 🆕
    }
  ) => {
    setLoading(true);
    try {
      if (options?.confirmMessage) {
        const result = await Swal.fire({
          title: options.confirmMessage,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: t("yes"),
          cancelButtonText: t("cancel"),
          customClass: {
            confirmButton:
              "bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700",
            cancelButton:
              "bg-gray-200 text-black px-4 py-2 rounded hover:bg-gray-300",
          },
        });

        if (!result.isConfirmed) {
          setLoading(false);
          return false;
        }
      }

      let Authorization = "";
      if (token === "owner") {
        Authorization = getOwnerAccessToken() as string;
      } else if (token === "store") {
        Authorization = getStoreAccessToken() as string;
      } else if (token === "admin") {
        Authorization = getAdminAccessToken();
      }

      if (options?.method === "PATCH") {
        await apiPatch(url, data, { Authorization });
      } else if (options?.method === "PUT") {
        await apiPut(url, data, { Authorization });
      } else if (options?.method === "DELETE") {
        await apiDelete(url, { Authorization });
      } else {
        await apiPost(url, data, { Authorization });
      }

      // Check response status before navigating
      if (options?.invalidateQueries) {
        options.invalidateQueries.forEach((key) =>
          queryClient.invalidateQueries({ queryKey: [key] })
        );
      }

      if (options?.successMessage) {
        showSuccess(options.successMessage);
      }

      if (options?.navigateAfter) {
        options.navigateAfter();
      }
      return true; // ✅ العملية تمت بنجاح
    } catch (err: any) {
      showError(err.message || "something wrong happened");
    } finally {
      setLoading(false);
    }
  };

  return { loading, updateResource };
}
